import React, { useMemo, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import './TeamMembersPicker.css';

export interface TeamMemberOption {
  value: string;
  label: string;
}

export interface TeamMembersPickerProps {
  label?: string;
  placeholder?: string;
  options: TeamMemberOption[];
  selectedIds: string[];
  onRemove: (id: string) => void;
  /** Called when a team member is selected from the dropdown */
  onAdd?: (id: string) => void;
  /** @deprecated Use onAdd instead. Called when user clicks into the search input (legacy modal approach) */
  onRequestAdd?: () => void;
  className?: string;
}

/**
 * TeamMembersPicker
 * Picker with inline dropdown: selected members as chips + a search input with dropdown.
 */
const TeamMembersPicker: React.FC<TeamMembersPickerProps> = ({
  label = 'Add Team Members',
  placeholder = 'Search and add team members...',
  options,
  selectedIds,
  onRemove,
  onAdd,
  onRequestAdd,
  className = '',
}) => {
  const [term, setTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedMembers = useMemo(() => {
    const map = new Map(options.map((opt) => [opt.value, opt.label]));
    return selectedIds
      .map((id) => ({ id, label: map.get(id) ?? id }))
      .filter(Boolean);
  }, [options, selectedIds]);

  // Filter options: exclude already selected and match search term
  const filteredOptions = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return options.filter(
      (opt) =>
        !selectedSet.has(opt.value) &&
        opt.label.toLowerCase().includes(term.toLowerCase())
    );
  }, [options, selectedIds, term]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (memberId: string) => {
    if (onAdd) {
      onAdd(memberId);
    }
    setTerm('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
    // If legacy onRequestAdd is provided and no onAdd, fall back to modal
    if (!onAdd && onRequestAdd) {
      onRequestAdd();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTerm(e.target.value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`team-members ${className}`} ref={containerRef}>
      <div className="team-members__label">{label}</div>

      {selectedMembers.length > 0 && (
        <div className="team-members__chips">
          {selectedMembers.map((member) => (
            <span key={member.id} className="team-members__chip">
              <span className="team-members__avatar">
                {member.label
                  .split(' ')
                  .map((p) => p[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <span className="team-members__chip-text">{member.label}</span>
              <button
                type="button"
                className="team-members__chip-remove"
                aria-label={`Remove ${member.label}`}
                onClick={() => onRemove(member.id)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="team-members__search-container">
        <div className="team-members__search">
          <FontAwesomeIcon icon={faSearch} className="team-members__icon" />
          <input
            ref={inputRef}
            className="team-members__input"
            placeholder={placeholder}
            value={term}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            aria-label={label}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          />
        </div>

        {/* Dropdown */}
        {isDropdownOpen && onAdd && (
          <div className="team-members__dropdown" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className="team-members__dropdown-item"
                  role="option"
                  tabIndex={0}
                  onClick={() => handleSelect(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(opt.value);
                    }
                  }}
                >
                  <span className="team-members__dropdown-avatar">
                    {opt.label
                      .split(' ')
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  <span className="team-members__dropdown-name">{opt.label}</span>
                  <FontAwesomeIcon icon={faUserPlus} className="team-members__dropdown-add-icon" />
                </div>
              ))
            ) : (
              <div className="team-members__dropdown-empty">
                {term ? `No members matching "${term}"` : 'All team members have been added'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMembersPicker;


